import React, { useState, useEffect, useRef } from 'react';
import { 
  UserCircle, Loader2, Save, Phone, Users, BookOpen, Calendar,
  Upload, FileText, Home, Heart, Camera, CheckCircle, AlertCircle, X
} from 'lucide-react';
import { studentPanelAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const StudentProfile = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [residenceTypes, setResidenceTypes] = useState([]);
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  
  const [formData, setFormData] = useState({
    phone: '',
    extra_phone: '',
    residence_type: '',
    residence_address: '',
    marital_status: ''
  });
  
  const [files, setFiles] = useState({
    photo: null,
    attestation: null,
    objective: null
  });
  
  const [previews, setPreviews] = useState({ photo: null });
  const [errors, setErrors] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  
  const photoRef = useRef(null);
  const attestationRef = useRef(null);
  const objectiveRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await studentPanelAPI.getProfile();
      const studentData = response.data.student;
      setStudent(studentData);
      setResidenceTypes(response.data.residence_types || []);
      setMaritalStatuses(response.data.marital_statuses || []);
      
      setFormData({
        phone: studentData.user?.phone || '',
        extra_phone: studentData.extra_phone || '',
        residence_type: studentData.residence_type || '',
        residence_address: studentData.residence_address || '',
        marital_status: studentData.marital_status || ''
      });
      
      if (studentData.photo_url) {
        setPreviews({ photo: getFileUrl(studentData.photo_url) });
      }
      
      checkMissingFields(studentData);
    } catch (error) {
      console.error('Profilni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const checkMissingFields = (data) => {
    const missing = [];
    if (!data.photo_url) missing.push("Talaba rasmi");
    if (!data.attestation_url) missing.push("Attestat");
    if (!data.objective_url) missing.push("Obektivka");
    if (!data.extra_phone) missing.push("Qo'shimcha telefon");
    if (!data.residence_type) missing.push("Turar joy turi");
    if (!data.residence_address) missing.push("Manzil");
    if (!data.marital_status) missing.push("Oilaviy holat");
    setMissingFields(missing);
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const handleFileChange = (field, file) => {
    setErrors(prev => ({ ...prev, [field]: null }));
    
    if (file) {
      const isPhoto = field === 'photo';
      const allowedTypes = isPhoto 
        ? ['image/jpeg', 'image/jpg', 'image/png']
        : ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = isPhoto ? 5 : 10;
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ 
          ...prev, 
          [field]: isPhoto ? "Faqat JPG/PNG rasm" : "Faqat PDF/JPG/PNG" 
        }));
        return;
      }
      if (file.size > maxSize * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [field]: `Max ${maxSize}MB` }));
        return;
      }
    }
    
    setFiles(prev => ({ ...prev, [field]: file }));
    
    if (field === 'photo' && file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviews({ photo: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    setSuccessMessage('');
    
    try {
      const data = new FormData();
      
      data.append('phone', formData.phone || '');
      data.append('extra_phone', formData.extra_phone || '');
      data.append('residence_type', formData.residence_type || '');
      data.append('residence_address', formData.residence_address || '');
      data.append('marital_status', formData.marital_status || '');
      
      if (files.photo) data.append('photo', files.photo);
      if (files.attestation) data.append('attestation', files.attestation);
      if (files.objective) data.append('objective', files.objective);
      
      const response = await studentPanelAPI.updateProfile(data);
      
      if (response.data.success) {
        if (response.data.missing_fields?.length > 0) {
          setMissingFields(response.data.missing_fields);
          setSuccessMessage("Saqlandi! Qolgan: " + response.data.missing_fields.join(", "));
        } else {
          setSuccessMessage("Profil to'liq saqlandi! ✓");
          setMissingFields([]);
        }
        await fetchProfile();
        setEditMode(false);
        setFiles({ photo: null, attestation: null, objective: null });
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Saqlashda xatolik yuz berdi' });
      }
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('uz-UZ');
  };

  // File upload button component
  const FileUpload = ({ field, label, icon: Icon, accept, currentUrl }) => {
    const refs = { photo: photoRef, attestation: attestationRef, objective: objectiveRef };
    const selectedFile = files[field];
    const hasFile = selectedFile || currentUrl;
    
    return (
      <div style={{ 
        padding: '12px', 
        background: errors[field] ? '#fef2f2' : hasFile ? '#f0fdf4' : '#f8fafc',
        borderRadius: '8px',
        border: `1px solid ${errors[field] ? '#fecaca' : hasFile ? '#bbf7d0' : '#e2e8f0'}`
      }}>
        <input 
          type="file" 
          ref={refs[field]} 
          hidden 
          accept={accept}
          onChange={(e) => handleFileChange(field, e.target.files[0])}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon size={18} color={hasFile ? '#16a34a' : '#64748b'} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{label}</span>
          </div>
          {editMode ? (
            <button 
              type="button"
              onClick={() => refs[field].current?.click()}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                background: hasFile ? '#dcfce7' : '#e0f2fe',
                color: hasFile ? '#166534' : '#0369a1',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {hasFile ? '✓ O\'zgartirish' : 'Yuklash'}
            </button>
          ) : (
            currentUrl ? (
              <a href={getFileUrl(currentUrl)} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '12px', color: '#2563eb' }}>Ko'rish →</a>
            ) : (
              <span style={{ fontSize: '12px', color: '#ef4444' }}>Yuklanmagan</span>
            )
          )}
        </div>
        {selectedFile && (
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#059669' }}>
            📎 {selectedFile.name}
          </div>
        )}
        {errors[field] && (
          <div style={{ marginTop: '4px', fontSize: '11px', color: '#dc2626' }}>
            ⚠️ {errors[field]}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  const user = student?.user;
  const group = student?.group;

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><UserCircle size={28} /> Mening profilim</h1>
        </div>
        {!editMode ? (
          <button className="btn btn-primary" onClick={() => setEditMode(true)}>
            Tahrirlash
          </button>
        ) : (
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => {
              setEditMode(false);
              setFiles({ photo: null, attestation: null, objective: null });
              setErrors({});
              fetchProfile();
            }}>
              Bekor qilish
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="spinner" size={18} /> : <Save size={18} />}
              Saqlash
            </button>
          </div>
        )}
      </header>

      {/* Alerts */}
      {successMessage && (
        <div style={{ 
          background: '#d1fae5', border: '1px solid #10b981', borderRadius: '10px', 
          padding: '12px 16px', marginBottom: '16px', display: 'flex', 
          alignItems: 'center', justifyContent: 'space-between'
        }}>
          <span style={{ color: '#065f46', fontSize: '14px' }}>✓ {successMessage}</span>
          <X size={18} style={{ cursor: 'pointer', color: '#065f46' }} onClick={() => setSuccessMessage('')} />
        </div>
      )}

      {errors.general && (
        <div style={{ 
          background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '10px', 
          padding: '12px 16px', marginBottom: '16px'
        }}>
          <span style={{ color: '#991b1b', fontSize: '14px' }}>⚠️ {errors.general}</span>
        </div>
      )}

      {!student?.profile_completed && missingFields.length > 0 && (
        <div style={{ 
          background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px', 
          padding: '12px 16px', marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', color: '#92400e' }}>
            <strong>⚠️ To'ldirilmagan:</strong> {missingFields.join(', ')}
          </div>
        </div>
      )}

      {/* Main Content - 2 Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
        
        {/* Left Column - Profile Card */}
        <div style={{ 
          background: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center'
        }}>
          {/* Avatar */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '16px' }}>
            {previews.photo ? (
              <img src={previews.photo} alt="" style={{ 
                width: '100px', height: '100px', borderRadius: '50%', 
                objectFit: 'cover', border: '3px solid #10b981'
              }} />
            ) : (
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '28px', fontWeight: '600', color: 'white'
              }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
            )}
            {editMode && (
              <button onClick={() => photoRef.current?.click()}
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: '#10b981', border: '2px solid white',
                  color: 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center'
                }}>
                <Camera size={16} />
              </button>
            )}
            <input type="file" ref={photoRef} hidden accept="image/jpeg,image/png"
              onChange={(e) => handleFileChange('photo', e.target.files[0])} />
          </div>
          
          <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: '#1e293b' }}>{user?.full_name}</h3>
          <div style={{ 
            display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
            background: '#dbeafe', color: '#1d4ed8', fontSize: '12px', fontWeight: '500'
          }}>
            {student?.student_id}
          </div>
          
          {student?.profile_completed ? (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#059669', fontSize: '13px' }}>
              <CheckCircle size={16} /> Profil to'liq
            </div>
          ) : (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#d97706', fontSize: '13px' }}>
              <AlertCircle size={16} /> To'liq emas
            </div>
          )}
          
          {/* Basic Info */}
          <div style={{ marginTop: '20px', textAlign: 'left', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Guruh</span>
              <span style={{ fontWeight: '500' }}>{group?.name || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Kurs</span>
              <span style={{ fontWeight: '500' }}>{group?.course || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#64748b' }}>Tug'ilgan</span>
              <span style={{ fontWeight: '500' }}>{formatDate(student?.birth_date)}</span>
            </div>
          </div>
        </div>

        {/* Right Column - Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Hujjatlar */}
          <div style={{ 
            background: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h4 style={{ margin: '0 0 16px', fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={18} /> Hujjatlar
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <FileUpload field="photo" label="Rasm 3x4" icon={Camera} accept="image/jpeg,image/png" currentUrl={student?.photo_url} />
              <FileUpload field="attestation" label="Attestat" icon={FileText} accept=".pdf,image/*" currentUrl={student?.attestation_url} />
              <FileUpload field="objective" label="Obektivka" icon={FileText} accept=".pdf,image/*" currentUrl={student?.objective_url} />
            </div>
          </div>

          {/* Bog'lanish */}
          <div style={{ 
            background: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h4 style={{ margin: '0 0 16px', fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Phone size={18} /> Bog'lanish
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Telefon</label>
                {editMode ? (
                  <input type="text" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+998 90 123 45 67"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                ) : (
                  <div style={{ padding: '10px 0', fontSize: '14px', fontWeight: '500' }}>{user?.phone || '-'}</div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
                  Qo'shimcha telefon <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {editMode ? (
                  <div>
                    <input type="text" value={formData.extra_phone}
                      onChange={(e) => { setFormData({...formData, extra_phone: e.target.value}); setErrors(p => ({...p, extra_phone: null})); }}
                      placeholder="+998 90 123 45 67"
                      style={{ 
                        width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                        border: `1px solid ${errors.extra_phone ? '#fecaca' : '#e2e8f0'}`,
                        background: errors.extra_phone ? '#fef2f2' : 'white'
                      }}
                    />
                    {errors.extra_phone && <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>⚠️ {errors.extra_phone}</div>}
                  </div>
                ) : (
                  <div style={{ padding: '10px 0', fontSize: '14px', fontWeight: '500', color: student?.extra_phone ? 'inherit' : '#ef4444' }}>
                    {student?.extra_phone || 'Kiritilmagan'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Turar joy */}
          <div style={{ 
            background: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h4 style={{ margin: '0 0 16px', fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Home size={18} /> Turar joy
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
                  Turi <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {editMode ? (
                  <select value={formData.residence_type}
                    onChange={(e) => { setFormData({...formData, residence_type: e.target.value}); setErrors(p => ({...p, residence_type: null})); }}
                    style={{ 
                      width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                      border: `1px solid ${errors.residence_type ? '#fecaca' : '#e2e8f0'}`
                    }}>
                    <option value="">Tanlang...</option>
                    {residenceTypes.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                  </select>
                ) : (
                  <div style={{ padding: '10px 0', fontSize: '14px', fontWeight: '500', color: student?.residence_type ? 'inherit' : '#ef4444' }}>
                    {student?.residence_type_display || 'Tanlanmagan'}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
                  Manzil <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {editMode ? (
                  <input type="text" value={formData.residence_address}
                    onChange={(e) => { setFormData({...formData, residence_address: e.target.value}); setErrors(p => ({...p, residence_address: null})); }}
                    placeholder="To'liq manzilni kiriting"
                    style={{ 
                      width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                      border: `1px solid ${errors.residence_address ? '#fecaca' : '#e2e8f0'}`
                    }}
                  />
                ) : (
                  <div style={{ padding: '10px 0', fontSize: '14px', fontWeight: '500', color: student?.residence_address ? 'inherit' : '#ef4444' }}>
                    {student?.residence_address || 'Kiritilmagan'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Shaxsiy */}
          <div style={{ 
            background: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h4 style={{ margin: '0 0 16px', fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Heart size={18} /> Shaxsiy ma'lumotlar
            </h4>
            <div style={{ maxWidth: '250px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
                Oilaviy holati <span style={{ color: '#ef4444' }}>*</span>
              </label>
              {editMode ? (
                <select value={formData.marital_status}
                  onChange={(e) => { setFormData({...formData, marital_status: e.target.value}); setErrors(p => ({...p, marital_status: null})); }}
                  style={{ 
                    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                    border: `1px solid ${errors.marital_status ? '#fecaca' : '#e2e8f0'}`
                  }}>
                  <option value="">Tanlang...</option>
                  {maritalStatuses.map(ms => <option key={ms.value} value={ms.value}>{ms.label}</option>)}
                </select>
              ) : (
                <div style={{ padding: '10px 0', fontSize: '14px', fontWeight: '500', color: student?.marital_status ? 'inherit' : '#ef4444' }}>
                  {student?.marital_status_display || 'Tanlanmagan'}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default StudentProfile;
